import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchActivityLogs,
  fetchActivityStats,
  fetchBatchSummaries,
  fetchBatchEmails,
  fetchBatchStatus,
  updateActivityLog,
  clearCurrentLog,
  clearBatchEmails
} from '../store/slices/activityLogSlice';
import { toast } from 'react-toastify';
import {
  EyeIcon,
  PencilIcon,
  ChartBarIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  InboxStackIcon
} from '@heroicons/react/24/outline';

const ActivityLogs = () => {
  const dispatch = useDispatch();
  const { 
    items, 
    loading, 
    total, 
    stats, 
    batches, 
    batchLoading,
    batchTotal,
    batchEmails,
    emailTotal,
    currentBatchStatus
  } = useSelector((state) => state.activityLogs);
  const { user } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [viewingLog, setViewingLog] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' or 'batches'
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    activityType: '',
    dateFrom: '',
    dateTo: '',
    responseReceived: '',
    page: 1
  });
  const [batchFilters, setBatchFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 1
  });
  const [emailPage, setEmailPage] = useState(1);

  // Check if user is superadmin
  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchActivityLogs(filters));
    }
  }, [dispatch, isSuperAdmin, filters]);

  useEffect(() => {
    if (isSuperAdmin && activeTab === 'batches') {
      dispatch(fetchBatchSummaries(batchFilters));
    }
  }, [dispatch, isSuperAdmin, activeTab, batchFilters]);

  useEffect(() => {
    if (isSuperAdmin && showStats) {
      dispatch(fetchActivityStats({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      }));
    }
  }, [dispatch, isSuperAdmin, showStats, filters.dateFrom, filters.dateTo]);

  // Polling for batch status when viewing a batch that's still processing
  useEffect(() => {
    if (selectedBatch && currentBatchStatus?.status === 'processing') {
      const interval = setInterval(() => {
        dispatch(fetchBatchStatus(selectedBatch.groupId));
        dispatch(fetchBatchEmails({ 
          groupId: selectedBatch.groupId, 
          params: { page: emailPage, limit: 100 }
        }));
      }, 5000); // Poll every 5 seconds

      setPollingInterval(interval);

      return () => clearInterval(interval);
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [selectedBatch, currentBatchStatus, emailPage, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleBatchFilterChange = (key, value) => {
    setBatchFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleViewBatch = async (batch) => {
    setSelectedBatch(batch);
    setEmailPage(1);
    await dispatch(fetchBatchEmails({ 
      groupId: batch.groupId, 
      params: { page: 1, limit: 100 }
    }));
    await dispatch(fetchBatchStatus(batch.groupId));
  };

  const handleCloseBatch = () => {
    setSelectedBatch(null);
    setEmailPage(1);
    dispatch(clearBatchEmails());
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const handleViewLog = (log) => {
    setViewingLog(log);
    setShowModal(true);
  };

  const handleUpdateResponse = async (logId, responseData) => {
    try {
      await dispatch(updateActivityLog({
        id: logId,
        data: {
          responseReceived: true,
          responseDate: new Date(),
          responseContent: responseData.content
        }
      })).unwrap();
      toast.success('Response updated successfully');
      setShowModal(false);
    } catch (err) {
      toast.error('Failed to update response');
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'EMAIL_SENT':
      case 'EMAIL_OPENED':
      case 'EMAIL_CLICKED':
      case 'EMAIL_BOUNCED':
      case 'EMAIL_REPLIED':
        return <EnvelopeIcon className="h-5 w-5" />;
      case 'PHONE_CALL':
        return <PhoneIcon className="h-5 w-5" />;
      case 'ATTACHMENT_SENT':
        return <DocumentIcon className="h-5 w-5" />;
      case 'USER_LOGIN':
      case 'USER_LOGOUT':
        return <UserIcon className="h-5 w-5" />;
      default:
        return <ClockIcon className="h-5 w-5" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'EMAIL_SENT':
      case 'EMAIL_OPENED':
        return 'text-blue-600 bg-blue-100';
      case 'EMAIL_REPLIED':
        return 'text-green-600 bg-green-100';
      case 'EMAIL_BOUNCED':
        return 'text-red-600 bg-red-100';
      case 'PHONE_CALL':
        return 'text-purple-600 bg-purple-100';
      case 'ATTACHMENT_SENT':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatActivityType = (type) => {
    if (!type) return 'Unknown Activity';
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Super Administrators can access activity logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Activity Logs & Audit Trail</h1>
          <p className="mt-2 text-base text-gray-600 font-medium">
            Track all system activities and communications ({activeTab === 'individual' ? total : batchTotal} {activeTab === 'individual' ? 'activities' : 'batch sends'})
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center px-5 py-3 bg-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <ChartBarIcon className="h-5 w-5 mr-2" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b-2 border-gray-200">
        <button
          onClick={() => setActiveTab('individual')}
          className={`px-6 py-3 font-bold text-base transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'individual'
              ? 'text-blue-600 border-b-4 border-blue-600 -mb-0.5'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          <EnvelopeIcon className="h-5 w-5" />
          Individual Emails
        </button>
        <button
          onClick={() => setActiveTab('batches')}
          className={`px-6 py-3 font-bold text-base transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'batches'
              ? 'text-blue-600 border-b-4 border-blue-600 -mb-0.5'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          <InboxStackIcon className="h-5 w-5" />
          Batch Sends
        </button>
      </div>

      {/* Statistics Dashboard */}
      {showStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Activity Types</h3>
            <div className="space-y-3">
              {stats.activityTypeCounts?.slice(0, 5).map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{formatActivityType(item.activityType)}</span>
                  <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Top Users</h3>
            <div className="space-y-3">
              {stats.userActivityCounts?.slice(0, 5).map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium truncate">{item.user?.name}</span>
                  <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Response Rate</h3>
            <div className="space-y-3">
              {stats.responseStats?.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">
                    {item.responseReceived ? 'Responded' : 'No Response'}
                  </span>
                  <span className={`text-sm font-bold px-2 py-1 rounded-lg ${item.responseReceived ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-100'
                    }`}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 rounded-2xl shadow-xl text-white">
            <h3 className="text-lg font-bold mb-4">Daily Activity</h3>
            <div className="text-center">
              <div className="text-4xl font-bold">
                {stats.dailyActivity?.reduce((sum, day) => sum + parseInt(day.count), 0) || 0}
              </div>
              <div className="text-sm opacity-90 mt-2">Last 30 days</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {activeTab === 'individual' && (
        <div className="bg-white p-5 rounded-2xl shadow-xl border border-gray-200">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-bold text-gray-700">Filters:</span>
            </div>

            <input
              type="text"
              placeholder="Search activities..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />

            <select
              value={filters.activityType}
              onChange={(e) => handleFilterChange('activityType', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-medium"
            >
              <option value="">All Activities</option>
              <option value="EMAIL_SENT">Email Sent</option>
              <option value="EMAIL_REPLIED">Email Replied</option>
              <option value="PHONE_CALL">Phone Call</option>
              <option value="MANUAL_CONTACT">Manual Contact</option>
              <option value="ATTACHMENT_SENT">Attachment Sent</option>
            </select>

            <select
              value={filters.responseReceived}
              onChange={(e) => handleFilterChange('responseReceived', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 font-medium"
            >
              <option value="">All Responses</option>
              <option value="true">Responded</option>
              <option value="false">No Response</option>
            </select>

            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />

            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
        </div>
      )}

      {/* Batch Filters */}
      {activeTab === 'batches' && (
        <div className="bg-white p-5 rounded-2xl shadow-xl border border-gray-200">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-bold text-gray-700">Filters:</span>
            </div>

            <input
              type="text"
              placeholder="Search by subject..."
              value={batchFilters.search}
              onChange={(e) => handleBatchFilterChange('search', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />

            <input
              type="date"
              value={batchFilters.dateFrom}
              onChange={(e) => handleBatchFilterChange('dateFrom', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />

            <input
              type="date"
              value={batchFilters.dateTo}
              onChange={(e) => handleBatchFilterChange('dateTo', e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
        </div>
      )}

      {/* Activity Logs Table */}
      {activeTab === 'individual' && (
        <>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Activity</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Response</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.map((log) => (
                  <tr key={log.id} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center ${getActivityColor(log.activityType)} shadow-sm`}>
                          {getActivityIcon(log.activityType)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatActivityType(log.activityType)}
                          </div>
                          <div className="text-sm text-gray-600 truncate max-w-xs">
                            {log.subject || log.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{log.user?.name}</div>
                      <div className="text-sm text-gray-500">{log.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.contact?.primaryName || log.recipientName || '-'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {log.recipientEmail || log.contact?.emails?.[0] || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="font-medium">{new Date(log.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs">{new Date(log.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.responseReceived ? (
                        <div className="flex items-center text-green-700 bg-green-100 px-3 py-1 rounded-full w-fit">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Responded</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-500 bg-gray-100 px-3 py-1 rounded-full w-fit">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">No Response</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewLog(log)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-all duration-200"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No activity logs found.</p>
        </div>
      )}
        </>
      )}

      {/* Batch Sends Section */}
      {activeTab === 'batches' && !selectedBatch && (
        <>
          {batchLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {batches.map((batch) => (
                <div
                  key={batch.groupId}
                  className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-200 cursor-pointer"
                  onClick={() => handleViewBatch(batch)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{batch.subject || 'No Subject'}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          batch.status === 'processing' 
                            ? 'bg-yellow-100 text-yellow-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {batch.status === 'processing' ? 'Processing' : 'Completed'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-3">
                        Sent by: <span className="font-medium">{batch.user?.name || 'Unknown'}</span> ({batch.user?.email || 'N/A'})
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <EnvelopeIcon className="h-4 w-4 text-blue-600" />
                          <span className="font-bold text-gray-900">{batch.totalEmails}</span>
                          <span className="text-gray-600">Total</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span className="font-bold text-green-700">{batch.successCount}</span>
                          <span className="text-gray-600">Success</span>
                        </div>
                        {batch.failedCount > 0 && (
                          <div className="flex items-center gap-2">
                            <XCircleIcon className="h-4 w-4 text-red-600" />
                            <span className="font-bold text-red-700">{batch.failedCount}</span>
                            <span className="text-gray-600">Failed</span>
                          </div>
                        )}
                        {batch.pendingCount > 0 && (
                          <div className="flex items-center gap-2">
                            <ClockIcon className="h-4 w-4 text-yellow-600" />
                            <span className="font-bold text-yellow-700">{batch.pendingCount}</span>
                            <span className="text-gray-600">Pending</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-sm text-gray-600">
                        {new Date(batch.firstSent).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(batch.firstSent).toLocaleTimeString()}
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 mt-2" />
                    </div>
                  </div>
                </div>
              ))}

              {batches.length === 0 && (
                <div className="text-center py-12">
                  <InboxStackIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No batch sends found.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Batch Detail View */}
      {activeTab === 'batches' && selectedBatch && (
        <div className="space-y-4">
          {/* Back Button and Batch Summary */}
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
            <button
              onClick={handleCloseBatch}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold mb-4"
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Back to Batch List
            </button>
            
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedBatch.subject || 'No Subject'}</h2>
                <p className="text-gray-600">Group ID: <span className="font-mono text-sm">{selectedBatch.groupId}</span></p>
                <p className="text-gray-600 mt-1">
                  Sent by: <span className="font-medium">{selectedBatch.user?.name}</span> ({selectedBatch.user?.email})
                </p>
              </div>
              
              {currentBatchStatus && (
                <div className="flex flex-col gap-2">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    currentBatchStatus.status === 'processing' 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {currentBatchStatus.status === 'processing' ? 'Processing...' : 'Completed'}
                  </span>
                  <div className="text-right text-sm text-gray-600">
                    {currentBatchStatus.completedCount} / {currentBatchStatus.totalEmails} sent
                  </div>
                </div>
              )}
            </div>

            {/* Progress Stats */}
            {currentBatchStatus && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="text-2xl font-bold text-gray-900">{currentBatchStatus.totalEmails}</div>
                  <div className="text-sm text-gray-600">Total Emails</div>
                </div>
                <div className="bg-green-50 p-4 rounded-xl">
                  <div className="text-2xl font-bold text-green-700">{currentBatchStatus.successCount}</div>
                  <div className="text-sm text-gray-600">Success</div>
                </div>
                <div className="bg-red-50 p-4 rounded-xl">
                  <div className="text-2xl font-bold text-red-700">{currentBatchStatus.failedCount}</div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-700">{currentBatchStatus.pendingCount}</div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </div>
            )}
          </div>

          {/* Email List */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Recipient</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Sent At</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {batchEmails.map((email) => (
                      <tr key={email.id} className="hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{email.recipientName || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{email.recipientEmail}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {email.contact?.primaryName || '-'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {email.contact?.country || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            email.status === 'SUCCESS' 
                              ? 'bg-green-100 text-green-700'
                              : email.status === 'FAILED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {email.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div className="font-medium">{new Date(email.createdAt).toLocaleDateString()}</div>
                          <div className="text-xs">{new Date(email.createdAt).toLocaleTimeString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewLog(email)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-all duration-200"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {batchEmails.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">No emails found in this batch.</p>
            </div>
          )}
        </div>
      )}

      {/* View Log Modal */}
      {showModal && viewingLog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Activity Details</h3>
                    <p className="text-gray-600 mt-1">{formatActivityType(viewingLog.activityType)}</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${getActivityColor(viewingLog.activityType)}`}>
                    {viewingLog.status}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-medium">User:</span> {viewingLog.user?.name} ({viewingLog.user?.email})
                      </div>
                      <div>
                        <span className="font-medium">Date:</span> {new Date(viewingLog.createdAt).toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Subject:</span> {viewingLog.subject || '-'}
                      </div>
                      <div>
                        <span className="font-medium">Recipient:</span> {viewingLog.recipientName || '-'} ({viewingLog.recipientEmail || '-'})
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Response Information</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-medium">Response Received:</span> {viewingLog.responseReceived ? 'Yes' : 'No'}
                      </div>
                      {viewingLog.responseReceived && (
                        <>
                          <div>
                            <span className="font-medium">Response Date:</span> {viewingLog.responseDate ? new Date(viewingLog.responseDate).toLocaleString() : '-'}
                          </div>
                          <div>
                            <span className="font-medium">Response Content:</span>
                            <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
                              {viewingLog.responseContent || 'No content recorded'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {viewingLog.description && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {viewingLog.description}
                    </p>
                  </div>
                )}

                {viewingLog.reason && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Reason/Purpose</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {viewingLog.reason}
                    </p>
                  </div>
                )}

                {viewingLog.attachments && viewingLog.attachments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {viewingLog.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <DocumentIcon className="h-4 w-4 mr-2" />
                          {attachment.name || attachment}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;